import ComponentManager from "../../componentFramework";
import UrlUtil from "../../scripts/UrlUtil";
import EventListenerManager from "../EventListenerManager";
import JsfJsCommand from "../JsfJsCommand";
import onDocumentLoaded from "../../scripts/onDocumentLoaded";
import loadScripts from "../../scripts/loadScripts";

/**
 */
@ComponentManager.register()
export default class Wysiwyg {
	public static componentStyleClass = "wysiwyg";

	private static readonly ckeditorScriptsList = [
		// TODO @Michael: Move these into Core and not a separate domain. Consider using NPM instead for ckeditor, and put our own plugins into Core.
		"https://code.plaudit.com/ckeditor/ckeditor.js?v=3",
		"https://code.plaudit.com/ckeditor/adapters/jquery.js?v=2"
	];
	private static loadingScriptsPromise: Promise<boolean>;
	private static scriptsLoaded: boolean = false;

	private readonly eventListenerManager = new EventListenerManager();
	private textarea: HTMLTextAreaElement | undefined;
	private inViewObserver: IntersectionObserver | undefined;

	constructor(private readonly rootElement: HTMLElement) {
		const setupInViewObserver = () => {
			this.inViewObserver = new IntersectionObserver(() => {
				// Since the Wysiwyg could have an ancestors that is hidden and therefore
				// hiding this.rootElement, we must use getComputedStyle() to check visibility
				// This works because this.rootElement does not have it set so it inherits....
				// ... at least I think that is why it works, so if CSS changes we may need to change this.
				if (
					this.inViewObserver !== undefined
					&& window.getComputedStyle(this.rootElement).visibility !== "hidden"
				) {
					this.inViewObserver.disconnect();
					this.inViewObserver = undefined;
					this.init();
				}
			});
			this.inViewObserver.observe(this.rootElement);
		};

		const onScriptsLoaded = () => {
			// Lazy setup the editor
			onDocumentLoaded(rootElement, setupInViewObserver);
			return true;
		}


		if (!Wysiwyg.scriptsLoaded) {
			if (Wysiwyg.loadingScriptsPromise === undefined) {
				Wysiwyg.loadingScriptsPromise = loadScripts(Wysiwyg.ckeditorScriptsList)
					.then(() => { Wysiwyg.scriptsLoaded = true; return true; })
					.catch(reason => {
						console.error("Unable to load CKEditor JavaScript", reason);
						return false;
					});
			}
			Wysiwyg.loadingScriptsPromise = Wysiwyg.loadingScriptsPromise.then(onScriptsLoaded);
		} else {
			onScriptsLoaded();
		}
	}

	@ComponentManager.on("removed")
	protected componentRemoved() {
		this.eventListenerManager.removeListeners();
		this.inViewObserver?.disconnect();
		const ckeditor = this.getCkEditorInstance();
		if (ckeditor !== undefined) {
			try {
				ckeditor.updateElement();
				ckeditor.destroy();
			} catch {
				// TODO (Michael) Upgrade ckeditor and then try removing the catch.
			}
		}
	}

	private init() {
		const doc = this.rootElement.ownerDocument;
		const jQuery = (window as any).jQuery;
		const urlUtil = UrlUtil.getInstance(doc.defaultView ?? window);

		this.textarea = this.rootElement.querySelector(".wysiwyg__textarea") as HTMLTextAreaElement;
		const $textarea = jQuery(this.textarea);

		const params: any = {
			siteBaseHref: this.rootElement.dataset.sitebasehref
		};
		if (this.rootElement.dataset.contentscss !== undefined) {
			params.contentsCSS = this.rootElement.dataset.contentscss;
		}
		if (this.rootElement.dataset.config !== undefined) {
			params.nextCustomConfig = this.rootElement.dataset.config;
		}
		const configParams = new URLSearchParams(params);

		const customConfig = urlUtil.toUrlString(urlUtil.toUrlObj("assets/ckeditor/config.js"), configParams);
		const contentsCss = urlUtil.applyBaseUrl("assets/ckeditor/ckeditor.css");

		$textarea.ckeditor(
			() => {
				// Height you can set on ckeditor in config.js is just the white editing area
				// but once setup, as it is when this method is called, we can resize it
				// to a very specific size.
				const ckeditor = this.getCkEditorInstance();

				this.overrideMaximizeCommand();

				ckeditor.resize("100%", "auto"); // This sets the white area: .cke_contents

				// First we set the height on the outer area, but since ckeditor needs the height set on
				// the white area (.cke_contents) for manual resizing to work, we move the calculated
				// height there (after everything is setup later) and set the outer area to "auto".
				const ckeChrome = this.rootElement.querySelector<HTMLElement>(".cke_chrome");
				let afterSetup;
				if (ckeChrome !== null && this.textarea !== undefined) {
					ckeChrome.style.height = this.textarea.offsetHeight + "px";
					afterSetup = () => {
						const ckeContents = this.rootElement.querySelector<HTMLElement>(".cke_contents");
						if (ckeContents !== null) {
							ckeContents.style.height = ckeContents.offsetHeight + "px";
							ckeChrome.style.height = "auto";
						}
					}
				}


				ckeditor.on("focus", this.onCkEditorFocus);
				ckeditor.on("change", this.onCkEditorChange);
				ckeditor.on("blur", this.onCkEditorBlur);

				// Connect the label with the component
				const label = doc.querySelector(`label[for="${this.textarea!.id}"]`);
				if (label !== null) {
					this.eventListenerManager.manage(label)
						.addEventListener("click", () => { ckeditor.focus(); });
				}

				this.rootElement.classList.add("isSetup");
				if (afterSetup !== undefined) {
					afterSetup();
				}
			},
			{
				customConfig: customConfig,
				contentsCss: contentsCss // TODO: when merge ckeditor-styles likely want to remove this
			}
		);
	}

	/**
	 * Override the "maximize" comment (aka fullscreen).
	 * FCKeditor does a lot of DOM manipulation to achieve fullscreen which causes issues.
	 * Since we are not supporting really old browsers we can use more modern features and greatly simplify.
	 * @private
	 */
	private overrideMaximizeCommand() {
		// Overrides parts of the maximize plugin: https://github.com/ckeditor/ckeditor4/blob/a786d6f43c17ef90c13b1cf001dbd00204a622b1/plugins/maximize/plugin.js#L306-L308

		const CKEDITOR = (window as any).CKEDITOR;
		const ckeditor = this.getCkEditorInstance();
		const docBody = this.rootElement.ownerDocument.body;

		ckeditor.commands.maximize.exec = function () {
			// Go fullscreen if the state is off.
			if (this.state == CKEDITOR.TRISTATE_OFF) {
				docBody.classList.add("js-wysiwyg--isFullscreen");
				(ckeditor.container.$ as HTMLElement).classList.add("isFullscreen");
			} else {
				docBody.classList.remove("js-wysiwyg--isFullscreen");
				(ckeditor.container.$ as HTMLElement).classList.remove("isFullscreen");
			}

			// Toggle the "state" property
			this.toggleState();

			// Toggle button label.
			const button = this.uiItems[0];
			if (button) {
				const label = (this.state == CKEDITOR.TRISTATE_OFF) ? ckeditor.lang.maximize.maximize : ckeditor.lang.maximize.minimize;
				const buttonNode = CKEDITOR.document.getById(button._.id);
				buttonNode.getChild(1).setHtml(label);
				buttonNode.setAttribute("title", label);
				buttonNode.setAttribute("href", 'javascript:void("' + label + '");');
			}

			ckeditor.fire("maximize", this.state);
		}
	}

	private getCkEditorInstance(): any {
		return this.textarea !== undefined ? (window as any).CKEDITOR.instances[this.textarea!.id] : undefined;
	}

	private onCkEditorFocus = (ev: any) => {
		// We trigger a 'ckeditor-focus' on the textarea so other code can detect the wysiwyg has focus
		this.textarea!.dispatchEvent(
			new CustomEvent("pseudoFocusIn", {bubbles: true, cancelable: false})
		);
	}

	private onCkEditorChange = (ev: any) => {
		this.getCkEditorInstance()?.updateElement();
		this.textarea!.dispatchEvent(new InputEvent("input", {bubbles: true, cancelable: false}));
	}

	private onCkEditorBlur = (ev: any) => {
		this.getCkEditorInstance()?.updateElement();
		this.textarea!.dispatchEvent(new Event("change", {bubbles: true, cancelable: false}));
	}
}


// =============================================================================================
// Perform some "legacy" setup so the file browser in CKEditor opens our files dialog and
// we can communicate the values back and forth.

// TODO @Michael: Update code so we can clean this up. Issues:
//		1) ContentEditorBean.java calls JS directly with: CoreUI.JS_PACKAGE_CORE_UI +".EditContent.SelectResourceBrowser.onDialogClose"
//		2) Our CKEditor plugin, "plauditpopup" has plugin.js which calls: PlauditCore.coreUI.EditContent.SelectResourceBrowser.openDialog

type EditContentObjectType = {
	SelectResourceBrowser: {
		openDialog: (type: string, protocol: string | null, value: string) => void;
		onDialogClose: (value: string | null) => void;
	}
};
type PlauditCoreHoldingWindow = Window & {
	selectResourceBrowser: (type: string, val: string) => void;
	PlauditCore: {
		coreUI: {
			EditContent: EditContentObjectType
		}
	}
};

const win: PlauditCoreHoldingWindow = window as any;

const makeEditContentObject = () => {
	return {
		SelectResourceBrowser: {
			openDialog: (type, protocol, value) => {
				win.selectResourceBrowser(type, protocol !== null ? protocol + value : value);
			},
			onDialogClose: (value) => {
				if (value !== null) {
					const plauditDialogPlugin = (window as any).CKEDITOR.plugins.get('plauditdialog');
					plauditDialogPlugin.setDialogValue(value);
				}
			}
		}
	} as EditContentObjectType;
}
if (win.PlauditCore === undefined) {
	win.PlauditCore = {
		coreUI: {
			EditContent: makeEditContentObject()
		}
	};
} else if (win.PlauditCore.coreUI === undefined) {
	win.PlauditCore.coreUI = {
		EditContent: makeEditContentObject()
	};
} else if (win.PlauditCore.coreUI.EditContent === undefined) {
	win.PlauditCore.coreUI.EditContent = makeEditContentObject();
}

if (win.selectResourceBrowser === undefined) {
	win.selectResourceBrowser = (type: string, val: string) => {
		const options = {
			params: {
				selectedValueType: type,
				selectedValue: val
			}
		};
		JsfJsCommand.run("selectResourceBrowser", document.body, options);
	};
}
